import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateProgramReq, setProgramReq, setDatabaseFetchLoading, setAvailableDatabases } from '../../../../store/modules/bilsoftSlice';
import { loginBilsoftForDatabaseId } from '../utils/bilsoftRequest';

import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

export default function ProgramReq({ req = null, title = null }) {
    const { programReq, databaseFetchLoading } = useSelector((state) => state.bilsoft);
    const dispatch = useDispatch();
    const toast = useRef(null);
    const isInitialized = useRef(false);

    // Initialize bilsoft-specific fields only once
    useEffect(() => {
        if (!isInitialized.current) {
            if (req) {
                // If req is provided (update mode), use existing data
                const reqData = {
                    taxNumber: req.taxNumber || '',
                    databaseId: req.databaseId || '',
                    period: req.period || '',
                    username: req.username || '',
                    password: req.password || '',
                    apiUsername: req.apiUsername || '',
                    apiPassword: req.apiPassword || ''
                };
                dispatch(setProgramReq(reqData));
            } else {
                // If no req (new entry mode), initialize empty
                const initialFields = {
                    taxNumber: '',
                    databaseId: '',
                    period: '',
                    username: '',
                    password: '',
                    apiUsername: '',
                    apiPassword: ''
                };
                dispatch(setProgramReq(initialFields));
            }
            isInitialized.current = true;
        }
    }, [dispatch, req]);

    const onProgramInputChange = (e, fieldName) => {
        dispatch(updateProgramReq({ title: fieldName, value: e.target.value }));
    };

    // Check if fetch database button should be enabled
    const canFetchDatabase = () => {
        return programReq?.taxNumber && programReq?.username && programReq?.password;
    };

    // Fetch database ID using the API
    const handleFetchDatabase = async () => {
        if (!canFetchDatabase()) {
            toast.current.show({
                severity: 'warn',
                summary: 'Uyarı',
                detail: 'Vergi numarası, kullanıcı adı ve şifre gereklidir',
                life: 3000
            });
            return;
        }

        dispatch(setDatabaseFetchLoading(true));

        try {
            const response = await loginBilsoftForDatabaseId(programReq.taxNumber, programReq.username, programReq.password);

            if (response.data.success && response.data.data && response.data.data.length > 0) {
                const databases = response.data.data;
                dispatch(setAvailableDatabases(databases));

                // Auto-fill the first database ID
                const firstDatabase = databases[0];
                dispatch(updateProgramReq({ title: 'databaseId', value: firstDatabase.veritabaniAd }));

                // Auto-fill period if available
                if (firstDatabase.firmaVeritabaniDonemDTO && firstDatabase.firmaVeritabaniDonemDTO.length > 0) {
                    dispatch(updateProgramReq({ title: 'period', value: firstDatabase.firmaVeritabaniDonemDTO[0].donemYil }));
                }

                toast.current.show({
                    severity: 'success',
                    summary: 'Başarılı',
                    detail: 'Veritabanı bilgileri getirildi',
                    life: 3000
                });
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Hata',
                    detail: 'Veritabanı bilgileri getirilemedi',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Database fetch error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Hata',
                detail: error?.response?.data?.message || 'Veritabanı bilgileri getirilemedi',
                life: 3000
            });
        } finally {
            dispatch(setDatabaseFetchLoading(false));
        }
    };

    const fieldLabels = {
        taxNumber: 'Vergi Numarası',
        databaseId: 'Veritabanı ID',
        period: 'Dönem Yıl',
        username: 'Kullanıcı Adı',
        password: 'Şifre',
        apiUsername: 'API Kullanıcı Adı',
        apiPassword: 'API Şifre'
    };

    const fieldOrder = ['taxNumber', 'username', 'password', 'databaseId', 'period', 'apiUsername', 'apiPassword'];

    return (
        <div className="w-full">
            <Toast ref={toast} />
            <form className="mt-5">
                <h3>Bilsoft Giriş Bilgileri</h3>
                {programReq &&
                    fieldOrder.map((fieldName, i) => {
                        const isDisabled = fieldName === 'databaseId';

                        return (
                            <div key={i} className="py-3">
                                <div className="w-full mb-2">
                                    <label htmlFor={fieldName}>{fieldLabels[fieldName]}</label>
                                </div>
                                <div className="w-full flex gap-2">
                                    <InputText id={fieldName} onChange={(e) => onProgramInputChange(e, fieldName)} placeholder={fieldLabels[fieldName]} value={programReq[fieldName] || ''} className="w-full" disabled={isDisabled} />
                                    {fieldName === 'databaseId' && (
                                        <Button
                                            label={programReq[fieldName] ? 'Veritabanı Güncelle' : 'Veritabanı Getir'}
                                            icon="pi pi-download"
                                            onClick={handleFetchDatabase}
                                            disabled={!canFetchDatabase()}
                                            loading={databaseFetchLoading}
                                            className="flex-shrink-0"
                                            tooltip={!canFetchDatabase() ? 'Vergi numarası, kullanıcı adı ve şifre gereklidir' : ''}
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </form>
        </div>
    );
}
